import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import useLGPDConsents from 'hooks/useLGPDConsents';

import { emitEvent, removeListeners } from 'lib/eventManager';
import {
  getFromLocalStorage,
  removeFromLocalStorage,
  setLocalStorage,
} from 'lib/localStorage';
import { get, post } from 'lib/request';
import { parseToBool } from 'lib/string';
import { sendSupixEvent } from 'lib/supix';
import { isDev } from 'lib/utils';

import { useLocalWishlist } from 'providers/LocalWishlistProvider';
import { useModal } from 'providers/ModalProvider';

import COOKIES from 'constants/cookies';
import LOCAL_STORAGE from 'constants/localStorage';
import USER from 'constants/user';

const LoginModal = dynamic(() => import('components/LoginModal'), {
  ssr: false,
});

const USER_LOCAL_STORAGE_CACHE = 1000 * 60 * 120; // 2 hours
const LEGACY_API_USER_ME_ENDPOINT = `https://${process.env.NEXT_PUBLIC_LEGACY_PAGES_TEMP_SUBDOMAIN}.promobit.com.br/User/me/`;

const isSupixUserIdAllowedByUser = parseToBool(
  getFromLocalStorage(LOCAL_STORAGE.IS_POLICY_NUMBER_1_ACCEPTED)
);

export const UserContext = createContext({
  loadingLoggedInUser: true,
  errorLoggedInUser: null,
  LoggedInUser: null,
  isLoggedIn: null,
  login() {},
  logout() {},
  refetchLoggedInUser() {},
  runCallbackIfLoggedIn() {},
});

const useUser = () => useContext(UserContext);

const UserProvider = ({ children, ssrAuthToken }) => {
  const { showModal } = useModal();
  const {
    postGiveLGPDConsents,
    verifyAndSendUserToAnalytics,
    resetLGPDConsentsStorage,
  } = useLGPDConsents();
  const {
    localOfferKeywords,
    localCouponKeywords,
    sendLocalOfferKeywordsToServer,
    sendLocalCouponKeywordsToServer,
  } = useLocalWishlist();
  const [user, setUser] = useState({
    loadingLoggedInUser: true,
    LoggedInUser: null,
    errorLoggedInUser: null,
    isLoadingLogout: false,
    isLoggedIn: false,
  });

  const getLoggedInUserFromServer = async ({ skipOldApiWorkaround }) => {
    try {
      if (!skipOldApiWorkaround) {
        try {
          // This is a workaround for creating a session for
          // the old web platform
          await get(LEGACY_API_USER_ME_ENDPOINT, {
            useDefaultAPI: false,
            ignoreErrors: true,
            credentials: 'include',
            headers: { 'Content-Type': null },
          });
        } catch {
          // Ignore errors
        }
      }

      const userObj = await get('/user/me');

      if (userObj) {
        setLocalStorage(
          LOCAL_STORAGE.LOGGED_IN_USER,
          JSON.stringify({
            ...userObj,
            expireDate: new Date().getTime() + USER_LOCAL_STORAGE_CACHE,
          })
        );

        const LoggedInUser = (await import('lib/LoggedInUser')).default;

        return new LoggedInUser(userObj);
      }
    } catch {
      setLocalStorage(LOCAL_STORAGE.LOGGED_IN_USER, null);
      return null;
    }

    setLocalStorage(LOCAL_STORAGE.LOGGED_IN_USER, null);
    return null;
  };

  const getLoggedInUser = async (
    { skipOldApiWorkaround } = { skipOldApiWorkaround: true }
  ) => {
    const token = getFromLocalStorage(LOCAL_STORAGE.ACCESS_TOKEN);

    if (!skipOldApiWorkaround) {
      try {
        const legacyApiPathWithOptionalUserToken = token
          ? `${LEGACY_API_USER_ME_ENDPOINT}?token=${token}`
          : LEGACY_API_USER_ME_ENDPOINT;

        // This is a workaround for getting an existing session from
        // the old web platform
        const userObj = await get(legacyApiPathWithOptionalUserToken, {
          useDefaultAPI: false,
          ignoreErrors: true,
          credentials: 'include',
          headers: { 'Content-Type': null },
        });

        if (userObj) {
          const { token: tokenFromOldApi } = userObj;

          if (tokenFromOldApi) {
            setLocalStorage(LOCAL_STORAGE.ACCESS_TOKEN, tokenFromOldApi);

            return getLoggedInUserFromServer({ skipOldApiWorkaround: true });
          }
        }

        return null;
      } catch (e) {
        const { captureException } = await import('@sentry/nextjs');

        captureException(e);

        return null;
      }
    }

    if (!token) {
      return null;
    }

    const loggedInUser = JSON.parse(
      getFromLocalStorage(LOCAL_STORAGE.LOGGED_IN_USER)
    );
    const isLoggedInUserCacheExpired =
      new Date().getTime() >= loggedInUser?.expireDate;

    if (!!loggedInUser && !isLoggedInUserCacheExpired) {
      const LoggedInUser = (await import('lib/LoggedInUser')).default;

      return new LoggedInUser(loggedInUser);
    }

    return getLoggedInUserFromServer({ skipOldApiWorkaround });
  };

  const checkLogin = async () => {
    const loggedInUser = await getLoggedInUser({
      skipOldApiWorkaround: process.env.NODE_ENV === 'development',
    });

    if (!loggedInUser) {
      removeFromLocalStorage(LOCAL_STORAGE.ACCESS_TOKEN);
      removeFromLocalStorage(LOCAL_STORAGE.LOGGED_IN_USER);
      removeFromLocalStorage(LOCAL_STORAGE.IS_SUPIX_COLLECTED);
    } else {
      // Workaround for legacy compat mode
      const { saveUserAsLegacyModeToLocalStorage } = await import(
        'lib/wishlist'
      );

      saveUserAsLegacyModeToLocalStorage(loggedInUser);
      verifyAndSendUserToAnalytics(loggedInUser);

      if (
        !getFromLocalStorage(LOCAL_STORAGE.IS_SUPIX_COLLECTED) &&
        isSupixUserIdAllowedByUser
      ) {
        await sendSupixEvent('/usa', { id: loggedInUser.userId });

        setLocalStorage(LOCAL_STORAGE.IS_SUPIX_COLLECTED, 'true');
      }
    }

    const isLoggedIn = loggedInUser !== null;

    setUser({
      errorLoggedInUser: null,
      loadingLoggedInUser: false,
      isLoggedIn,
      isLoadingLogout: false,
      LoggedInUser: loggedInUser,
    });

    return isLoggedIn;
  };

  const login = useCallback(async ({ token, reloadOnLogin = true }) => {
    try {
      const { setCookies } = await import('cookies-next');
      const dayjs = (await import('dayjs')).default;

      setLocalStorage(LOCAL_STORAGE.ACCESS_TOKEN, token);
      setCookies(COOKIES.IS_USER_LOGGED, true, {
        expires: dayjs().add(1, 'year').toDate(),
        sameSite: true,
      });

      removeFromLocalStorage(LOCAL_STORAGE.SHOWED_WELCOME_NOTIFICATION);

      const { removeFromSessionStorage } = await import('lib/sessionStorage');

      removeFromSessionStorage('utm_campaign');

      setUser((lastUserState) => ({
        ...lastUserState,
        errorLoggedInUser: null,
        loadingLoggedInUser: false,
      }));

      if (localOfferKeywords.length > 0) {
        await sendLocalOfferKeywordsToServer();
      }

      if (localCouponKeywords.length > 0) {
        await sendLocalCouponKeywordsToServer();
      }

      if (getFromLocalStorage(LOCAL_STORAGE.WEBPUSH)) {
        await post('/webpush', { body: { user_auth_token: token } });
      }

      await postGiveLGPDConsents();

      if (reloadOnLogin) {
        window.location.reload();
      }
    } catch (error) {
      setUser((lastUserState) => ({
        ...lastUserState,
        loadingLoggedInUser: false,
        errorLoggedInUser: error,
      }));
    }
  }, []);

  useEffect(() => {
    if (ssrAuthToken) {
      const localStorageToken = getFromLocalStorage(LOCAL_STORAGE.ACCESS_TOKEN);

      if (!localStorageToken) {
        login({ token: ssrAuthToken, reloadOnLogin: false });
      }
    }

    const emitLoginEvent = async () => {
      const isLogged = await checkLogin();

      emitEvent(
        USER.EVENTS.LOGIN_CHECKED,
        { isLogged },
        { storeDispatch: true }
      );
    };

    emitLoginEvent();

    return () => {
      removeListeners(USER.EVENTS.LOGIN_CHECKED);
    };
  }, []);

  useEffect(() => {
    const reloadPageIfApiTokenIsRemoved = () => {
      if (
        !getFromLocalStorage(LOCAL_STORAGE.ACCESS_TOKEN) &&
        getFromLocalStorage(LOCAL_STORAGE.SHOULD_RELOAD_ON_LOGOUT)
      ) {
        removeFromLocalStorage(LOCAL_STORAGE.SHOULD_RELOAD_ON_LOGOUT);
        window.location.reload();
      }
    };

    window.addEventListener('storage', reloadPageIfApiTokenIsRemoved);

    return () => {
      window.removeEventListener('storage', reloadPageIfApiTokenIsRemoved);
    };
  }, []);

  const logout = useCallback(async () => {
    const { removeCookies } = await import('cookies-next');

    removeFromLocalStorage(LOCAL_STORAGE.ACCESS_TOKEN);
    removeFromLocalStorage(LOCAL_STORAGE.IS_DARK_MODE_ACTIVE);
    removeFromLocalStorage(LOCAL_STORAGE.LOGGED_IN_USER);
    removeFromLocalStorage(LOCAL_STORAGE.IS_SUPIX_COLLECTED);
    removeFromLocalStorage(LOCAL_STORAGE.SHOWED_WELCOME_NOTIFICATION);
    removeFromLocalStorage(LOCAL_STORAGE.WEBPUSH_HASH);
    setLocalStorage(LOCAL_STORAGE.SHOULD_RELOAD_ON_LOGOUT, 'true');
    removeCookies(COOKIES.IS_USER_LOGGED);

    resetLGPDConsentsStorage();

    setUser((lastUserState) => ({
      ...lastUserState,
      LoggedInUser: null,
      isLoggedIn: false,
      isLoadingLogout: true,
    }));

    // Workaround for legacy compat logout
    if (isDev()) {
      window.location.reload();
      return;
    }

    window.location.assign(
      `https://${process.env.NEXT_PUBLIC_LEGACY_PAGES_TEMP_SUBDOMAIN}.promobit.com.br/User/logout`
    );
  }, []);

  /**
   * Same as `login` but skip loading the user from `localStorage` cache
   */
  const refetchLoggedInUser = useCallback(async () => {
    try {
      const loggedInUser = await getLoggedInUser({
        skipOldApiWorkaround: process.env.NODE_ENV === 'development',
      });

      setUser((lastUserState) => ({
        ...lastUserState,
        errorLoggedInUser: null,
        loadingLoggedInUser: false,
        isLoggedIn: true,
        LoggedInUser: loggedInUser,
      }));
    } catch (errorLoggedInUser) {
      setUser((lastUserState) => ({
        ...lastUserState,
        errorLoggedInUser,
        loadingLoggedInUser: false,
        isLoggedIn: false,
      }));
    }

    return true;
  }, []);

  const runCallbackIfLoggedIn = useCallback(
    (callback, ...args) => {
      const { loadingLoggedInUser, isLoggedIn } = user;

      if (loadingLoggedInUser) {
        return;
      }

      if (!isLoggedIn) {
        showModal(LoginModal, { isOpen: true });
        return;
      }

      callback(...args);
    },
    [user.loadingLoggedInUser]
  );

  return (
    <UserContext.Provider
      value={{
        ...user,
        login,
        logout,
        refetchLoggedInUser,
        runCallbackIfLoggedIn,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

UserProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default UserProvider;

export { useUser };
